import { MapPin, MoreHorizontal, Plus, Search, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getListVenuesQueryKey, useListVenues } from '@/api/generated/venues';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export default function VenueList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: result, isLoading: loading } = useListVenues({
    query: {
      queryKey: getListVenuesQueryKey(),
    },
  });
  const venues = result?.data || [];

  const renderRows = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center py-10 text-gray-400">
            読み込み中...
          </TableCell>
        </TableRow>
      );
    }
    if (venues.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center py-10 text-gray-400">
            会場が登録されていません。
          </TableCell>
        </TableRow>
      );
    }
    return venues.map((venue) => (
      <TableRow key={venue.id} className="group hover:bg-gray-50">
        <TableCell className="font-mono text-xs text-gray-500">{venue.code}</TableCell>
        <TableCell>
          <div className="font-medium text-gray-900">{venue.name}</div>
        </TableCell>
        <TableCell>
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="w-3 h-3 mr-1 text-gray-400" />
            {venue.address || '未設定'}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center text-sm text-gray-600">
            <Users className="w-3 h-3 mr-1 text-gray-400" />
            {venue.capacity ? `${venue.capacity} 名` : '制限なし'}
          </div>
        </TableCell>
        <TableCell>
          {venue.isActive ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              有効
            </Badge>
          ) : (
            <Badge variant="secondary">無効</Badge>
          )}
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate(`/admin/master/venues/${venue.id}`)}>編集</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">削除</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('menu.venues')}</h1>
          <p className="text-gray-500 mt-1">試験会場の場所と収容人数を管理します。</p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700 shadow-md"
          onClick={() => navigate('/admin/master/venues/new')}
        >
          <Plus className="w-4 h-4 mr-2" /> 新規会場追加
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input placeholder="会場名・コードで検索..." className="pl-9" />
        </div>
      </div>

      {/* Venue Table */}
      <Card className="border-t-4 border-t-emerald-500 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>会場コード</TableHead>
                <TableHead>会場名</TableHead>
                <TableHead>住所</TableHead>
                <TableHead>収容人数</TableHead>
                <TableHead>ステータス</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{renderRows()}</TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
